// Backend state client. Stores tuning preferences + tuned-string records to the
// backend JSON (transition store before DB tables). Falls back to localStorage
// when offline / backend unreachable (NFR-7).

export interface TuningRecord {
  string: string; // 'G' | 'D' | 'A' | 'E'
  tuned: boolean;
  cents: number;
  timestamp: number;
}

export interface BackendState {
  preferences: {
    tuningVolumeThreshold: number; // 0-100
    tuningSmoothing: number; // 0-100
  };
  tuning_results: TuningRecord[];
}

const API_URL = '/api/state';
const LS_KEY = 'violinlab.backendState';

const DEFAULT_STATE: BackendState = {
  preferences: { tuningVolumeThreshold: 12, tuningSmoothing: 60 },
  tuning_results: [],
};

function loadLocal(): BackendState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { ...DEFAULT_STATE, preferences: { ...DEFAULT_STATE.preferences } };
    const parsed = JSON.parse(raw);
    return {
      preferences: { ...DEFAULT_STATE.preferences, ...parsed.preferences },
      tuning_results: parsed.tuning_results ?? [],
    };
  } catch {
    return { ...DEFAULT_STATE, preferences: { ...DEFAULT_STATE.preferences } };
  }
}

function saveLocal(state: BackendState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

// Load full state. Tries backend first, caches to localStorage; falls back to
// localStorage on any error.
export async function getState(): Promise<BackendState> {
  try {
    const res = await fetch(API_URL, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as Partial<BackendState>;
    const state: BackendState = {
      preferences: { ...DEFAULT_STATE.preferences, ...data.preferences },
      tuning_results: data.tuning_results ?? [],
    };
    saveLocal(state);
    return state;
  } catch {
    return loadLocal();
  }
}

// Deep-merge a patch into backend state. Falls back to local on failure.
export async function patchState(patch: Partial<BackendState>): Promise<BackendState> {
  try {
    const res = await fetch(API_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const state = (await res.json()) as BackendState;
    saveLocal(state);
    return state;
  } catch {
    // Fallback: merge locally.
    const local = loadLocal();
    if (patch.preferences) local.preferences = { ...local.preferences, ...patch.preferences };
    if (patch.tuning_results) {
      const byStr = new Map(local.tuning_results.map((r) => [r.string, r]));
      for (const rec of patch.tuning_results) byStr.set(rec.string, rec);
      local.tuning_results = [...byStr.values()];
    }
    saveLocal(local);
    return local;
  }
}

export function tunedStringsSet(results: TuningRecord[]): Set<string> {
  return new Set(results.filter((r) => r.tuned).map((r) => r.string));
}
